// Canned operator-facing copy for every "interesting" API security
// event kind we surface in the AsecEventDrawer.
//
// The drawer keys this map by `(category, kind)` where:
//   - category ∈ { "auth", "spec", "graphql", "api" }
//   - kind     = the `error_kind` (auth), `failure_kind` (spec/graphql),
//               or `block_reason` (api-observations) string echoed by
//               pingora's ctx structs.
//
// Source-of-truth enum lists (cross-checked 2026-06-01 against the
// pingora workspace at /opt/experimental-pingora/layerd-pingora/src/):
//
//   • auth.error_kind         — src/auth/ctx.rs::AuthErrorKind
//   • spec.failure_kind       — src/spec_validator/ctx.rs::FailureKind
//   • graphql.failure_kind    — src/graphql/ctx.rs::FailureKind
//   • api.block_reason        — src/proxy.rs:5637-5685 (compose site)
//
// Copy guidelines (do not vendor-marketing-ify):
//   • title          → 1 short sentence, no period, ≤ 56 chars
//   • what_happened  → operator-readable explanation, name the IdP /
//                      JWKS / spec / etc. by role, not by jargon
//   • recommendations→ array of next-action strings; reference panel
//                      pages the operator should *click on next*
//   • severity       → "low" | "medium" | "high" (drives chip palette)
//
// Use `lookupEventMeta(category, kind)` to read this — it returns
// `null` when no entry exists so the drawer can render a graceful
// fallback ("Engineering is gathering documentation for this event
// kind").

// ─── Auth — AuthErrorKind ────────────────────────────────────────────
// 14 entries. Order matches `pub enum AuthErrorKind` in
// /opt/experimental-pingora/layerd-pingora/src/auth/ctx.rs:117-132.
const AUTH = {
  bad_signature: {
    title: "Auth: token signature invalid",
    what_happened:
      "The presented JWT's signature did not validate against the configured JWKS or HMAC secret. Most often this means the token was issued by a different IdP than the one configured for this site, or the IdP rotated its signing keys and pingora's cached JWKS hasn't refreshed yet.",
    recommendations: [
      "Open API Security → Auth → IdP Connectors and confirm the issuer field on the connector matches the `iss` claim on the rejected token.",
      "If keys were recently rotated, click Refresh JWKS on the connector to drop the stale cache.",
      "Repeated bad-signature attempts from one IP look like credential probing — consider a rate-limit rule under Web App Security → Rate Limit.",
    ],
    severity: "high",
  },
  expired: {
    title: "Auth: token expired",
    what_happened:
      "The JWT's `exp` claim is in the past relative to now (with the configured leeway window applied — default 60 s). The caller is presenting a token that was valid earlier but has since aged out.",
    recommendations: [
      "If the caller is a server-to-server client, confirm it is refreshing tokens before they expire.",
      "If the rate of expired tokens is spiking sharply, the IdP may be issuing very short-lived tokens — check the IdP's `expires_in` setting.",
      "Increase Auth Policy → Leeway seconds if a known clock-skew situation between caller and pingora is unavoidable.",
    ],
    severity: "low",
  },
  not_yet_valid: {
    title: "Auth: token not yet valid (nbf)",
    what_happened:
      "The JWT's `nbf` (not-before) claim is in the future relative to now. The token has been issued for use later but the caller is presenting it now. This is almost always a clock-skew problem on the caller side, not malice.",
    recommendations: [
      "Verify the caller's system clock is synced (NTP) — large drift is the usual cause.",
      "If the IdP is mis-stamping `nbf`, the operator should check the IdP's clock as well.",
      "Increase Auth Policy → Leeway seconds as a stopgap if you cannot fix the clock immediately.",
    ],
    severity: "low",
  },
  bad_issuer: {
    title: "Auth: token issuer not accepted",
    what_happened:
      "The JWT's `iss` claim does not match any of the issuers configured on any active IdP connector for this site. Either the caller is using a token meant for a different tenant, or you have an IdP enrolled in your IdP but not in pingora.",
    recommendations: [
      "Open API Security → Auth → IdP Connectors. The accepted issuer is shown on every connector card.",
      "If you intended to accept this issuer, add a new connector with that exact `iss` URL.",
      "Repeated calls with foreign issuers from one IP look like cross-tenant token-substitution probes — block the IP under Web App Security → Firewall.",
    ],
    severity: "high",
  },
  bad_audience: {
    title: "Auth: token audience not accepted",
    what_happened:
      "The JWT's `aud` claim doesn't include any of the values the IdP connector accepts. The token is signed correctly but issued for a different audience (e.g. a different API, a different OAuth client).",
    recommendations: [
      "On the IdP connector, confirm the Audience field matches the value the issuing app uses for this API.",
      "If a caller legitimately needs to use this token, add their `aud` value to the connector's accepted-audience list.",
    ],
    severity: "medium",
  },
  bad_alg: {
    title: "Auth: JWT alg not allowed",
    what_happened:
      "The JWT's header specifies a signature algorithm that the IdP connector doesn't accept. A frequent cause is `alg: none` (algorithm-stripping attack) or downgrading from RS256 to HS256.",
    recommendations: [
      "If this looks deliberate (`none`, weak HS algos), the source IP is likely probing for the classic JWT algorithm-confusion CVE — block it under Web App Security → Firewall.",
      "On the IdP connector, confirm the allowed algorithms list matches what the IdP actually signs with.",
    ],
    severity: "high",
  },
  bad_hmac: {
    title: "Auth: HMAC signature invalid",
    what_happened:
      "The HMAC signature on the request did not validate against the configured shared secret. Either the secret is wrong, the canonicalisation rules don't match, or someone is presenting a stale signature.",
    recommendations: [
      "Open API Security → Auth → HMAC Keys and confirm the key the caller is using is enabled.",
      "Check the caller's signing implementation against the documented canonicalisation rules (method + path + body hash + timestamp).",
      "Repeated HMAC failures from one IP look like brute-force — block via Web App Security → Rate Limit.",
    ],
    severity: "high",
  },
  not_found: {
    title: "Auth: identity not found",
    what_happened:
      "The presented credential (API key, HMAC key id, or session id) has no matching record in pingora's identity store. Either it was never issued, was deleted, or the caller is guessing.",
    recommendations: [
      "Open API Security → Auth → API Keys / HMAC Keys and confirm the credential exists and is enabled.",
      "If you see a high rate of not_found from one IP, treat it as enumeration and block the IP under Web App Security → Firewall.",
    ],
    severity: "medium",
  },
  revoked: {
    title: "Auth: credential revoked",
    what_happened:
      "The credential is known to pingora but has been explicitly revoked (manually, or by an automated rotation policy). The caller is still using the old credential after rotation.",
    recommendations: [
      "Open API Security → Auth → API Keys and confirm the key shows as Revoked.",
      "Notify the credential's owner that they need to roll their integration to the new key.",
    ],
    severity: "medium",
  },
  bad_apikey: {
    title: "Auth: API key invalid",
    what_happened:
      "The presented Bearer token doesn't match any active API key for this site. Either the key prefix doesn't match a known issuance, or the secret half failed the constant-time compare against the stored HMAC-SHA256+pepper hash.",
    recommendations: [
      "Open API Security → Auth → API Keys and confirm the key the caller is using is enabled.",
      "Spike in failures from one IP looks like API-key brute force — block via Web App Security → Rate Limit.",
    ],
    severity: "high",
  },
  bad_password: {
    title: "Auth: basic-auth password invalid",
    what_happened:
      "The HTTP Basic credential failed verification. Either the username doesn't exist or the password didn't match.",
    recommendations: [
      "If basic auth is unintentionally exposed on this route, remove it from the Auth Policy and require a stronger credential type.",
      "Repeated bad-password attempts from one IP are credential stuffing — block via Web App Security → Rate Limit with a tight quota.",
    ],
    severity: "high",
  },
  bad_cert: {
    title: "Auth: mTLS client cert rejected",
    what_happened:
      "The TLS client certificate presented by the caller failed validation. Either the cert chain doesn't anchor to the configured CA bundle, the cert is expired or revoked, or the Subject / SAN didn't match an allowed identity.",
    recommendations: [
      "Open API Security → Auth → mTLS settings and confirm the CA bundle is current and the allowed-Subject list matches what your issuing CA produces.",
      "If the caller's cert was reissued, they may be presenting the old chain — ask them to roll.",
    ],
    severity: "high",
  },
  replay: {
    title: "Auth: replay detected",
    what_happened:
      "The same nonce (HMAC timestamp+nonce pair, or session cookie nonce) has been used in the recent dedupe window. Either the caller is retrying with a stale signed request, or the credential has been captured and is being replayed by a third party.",
    recommendations: [
      "If this is from a well-known caller IP, check that their HTTP client isn't retrying signed requests verbatim.",
      "Unexpected replays from a new IP look like credential capture — block the IP under Web App Security → Firewall and rotate the affected credential immediately.",
    ],
    severity: "high",
  },
  other: {
    title: "Auth: rejected (other)",
    what_happened:
      "The auth dispatcher rejected the request for a reason that didn't fall into one of the named buckets — usually a malformed credential format (e.g. JWT with fewer than 3 dot-separated parts, opaque API-key in the wrong header).",
    recommendations: [
      "Open the Raw Event section below — the `auth_event.method_used` and `auth_event.identity_kind` columns narrow this down.",
      "If this is the only failure mode you see for a caller, ask them to re-check the documented credential format.",
    ],
    severity: "low",
  },
};

// ─── Spec validator — FailureKind ────────────────────────────────────
// Actual enum (cross-checked 2026-06-01 in
// /opt/experimental-pingora/layerd-pingora/src/spec_validator/ctx.rs:150-191).
// Note: the plan listed legacy/aliased names; the values below are the
// real `label()` strings pingora emits onto the CH `failure_kind`
// column.
const SPEC = {
  path_not_found: {
    title: "Spec: path not declared",
    what_happened:
      "The request path didn't match any operation in the active OpenAPI spec. Under closed-world enforcement pingora returns 404 to keep the API surface honest — the operation isn't part of the published contract.",
    recommendations: [
      "If this *should* be a valid endpoint, open API Security → Discovery and look for an unreviewed candidate matching this path — promote it to the active spec.",
      "If this is legitimately unknown traffic, the source IP is probing for hidden endpoints — block under Web App Security → Firewall.",
      "If the spec is incomplete, lower the rejection severity by switching Spec Policy → Closed-world to log-only while you backfill.",
    ],
    severity: "medium",
  },
  method_not_allowed: {
    title: "Spec: method not allowed",
    what_happened:
      "The path exists in the active spec but doesn't declare the HTTP method the caller used (e.g. spec has GET /users but caller sent POST /users).",
    recommendations: [
      "If the spec is missing a real method, add it to the OpenAPI document and re-upload under API Security → Schemas.",
      "If this method is genuinely unsupported, the caller has a stale client — check their SDK version.",
    ],
    severity: "low",
  },
  bad_path_param: {
    title: "Spec: bad path parameter",
    what_happened:
      "A path parameter (e.g. `{id}`) failed the spec's declared schema — wrong type (string vs int), out-of-range, or fails a `pattern` regex.",
    recommendations: [
      "Open API Security → Events and filter to this same operation to see whether a specific caller is misshaping the URL.",
      "If the spec's parameter constraints are stricter than necessary, relax them in the OpenAPI document.",
    ],
    severity: "low",
  },
  bad_query: {
    title: "Spec: bad query parameter",
    what_happened:
      "A query-string parameter failed the spec's declared schema — wrong type, missing required, or violates an enum/pattern constraint. The failing parameter name is shown in `failure_field` on the row.",
    recommendations: [
      "Verify the caller is using the documented query-parameter set — older SDKs sometimes send deprecated params.",
      "If the spec is stricter than the production behaviour, soften the constraint in the OpenAPI document.",
    ],
    severity: "low",
  },
  bad_header: {
    title: "Spec: bad request header",
    what_happened:
      "A required header is missing, or a declared header failed its schema. The failing header name is in `failure_field` on the row.",
    recommendations: [
      "If a known caller is missing a header (Authorization, X-Api-Version), they likely have a misconfigured client.",
      "Check the OpenAPI document under `parameters: [{in: header}]` to confirm the constraint is intentional.",
    ],
    severity: "low",
  },
  bad_body_schema: {
    title: "Spec: request body schema violation",
    what_happened:
      "The request body parsed as JSON but failed the spec's declared schema — wrong type for a field, additional/unknown fields, or violates a `required`/`pattern`/`format` constraint.",
    recommendations: [
      "Open the Raw Event section below — `failure_field` (when populated) names the failing JSON path inside the body.",
      "If your producers send slightly different shapes from what the spec declares, update the OpenAPI document.",
    ],
    severity: "medium",
  },
  missing_required_field: {
    title: "Spec: missing required field",
    what_happened:
      "The request omitted a field the spec marks as `required`. The field name is in `failure_field`.",
    recommendations: [
      "Confirm the caller is using a current client — older SDKs may not populate fields added later in the contract.",
      "If a field has actually become optional, update the OpenAPI document so the contract reflects reality.",
    ],
    severity: "low",
  },
  body_too_large: {
    title: "Spec: request body too large",
    what_happened:
      "The request body exceeded the spec's body-size cap (or pingora's defensive ceiling, whichever is smaller). The body was never parsed, so no field-level detail is available.",
    recommendations: [
      "If this is legitimate traffic (file uploads, batch operations), raise the body-size limit on the operation in the OpenAPI document.",
      "Unsolicited huge bodies from one IP look like DoS — block under Web App Security → Firewall.",
    ],
    severity: "medium",
  },
  malformed_body: {
    title: "Spec: malformed body",
    what_happened:
      "The request body could not be parsed at all (invalid JSON, broken multipart, mismatched Content-Type). No schema-level detail is available because parsing failed before validation could run.",
    recommendations: [
      "Confirm the caller is sending the Content-Type the operation expects.",
      "Spike of malformed bodies from one IP looks like fuzzing — block under Web App Security → Firewall.",
    ],
    severity: "medium",
  },
};

// ─── GraphQL — FailureKind ───────────────────────────────────────────
// Cross-checked 2026-06-01 in
// /opt/experimental-pingora/layerd-pingora/src/graphql/ctx.rs:74-128.
// 9 enum variants — labels match the strings pingora writes to the
// `failure_kind` LowCardinality column.
const GRAPHQL = {
  malformed: {
    title: "GraphQL: malformed query",
    what_happened:
      "Apollo's parser couldn't tokenise the query at all — it isn't valid GraphQL syntax. Either a tooling bug is corrupting the body, or someone is fuzzing your endpoint.",
    recommendations: [
      "Check whether a known caller has shipped a broken client.",
      "Spike of malformed queries from one IP — block under Web App Security → Firewall.",
    ],
    severity: "low",
  },
  not_persisted: {
    title: "GraphQL: persisted-query not in allowlist",
    what_happened:
      "Persisted-query enforcement is on for this site, and the query the caller sent isn't on the allowlist. Either it's a brand-new query nobody has registered, or someone is bypassing the allowlist.",
    recommendations: [
      "Open API Security → GraphQL → Persisted Queries to register the new query hash.",
      "If the caller is rotating queries to evade the allowlist, treat as abuse — block under Web App Security → Firewall.",
    ],
    severity: "medium",
  },
  too_deep: {
    title: "GraphQL: query too deep",
    what_happened:
      "The query's nested-field depth exceeded the configured limit (default 10). Deep queries are how attackers force expensive joins — pingora caps depth before the resolver runs.",
    recommendations: [
      "If a legitimate caller needs deeper queries, raise the depth limit on this site under API Security → GraphQL.",
      "Repeated deep queries from one IP look like denial-of-service probing — block under Web App Security → Firewall.",
    ],
    severity: "medium",
  },
  too_complex: {
    title: "GraphQL: query too complex",
    what_happened:
      "The query's complexity score (each field is 1; list arguments multiply, capped at 100 per list) exceeded the configured limit (default 1000). This is the cost-aware sibling of the depth limit.",
    recommendations: [
      "If a legitimate caller's queries are getting heavier, raise the complexity limit on this site under API Security → GraphQL.",
      "Use the `complexity_observed` value on the row to see how far over the limit they were.",
    ],
    severity: "medium",
  },
  too_many_aliases: {
    title: "GraphQL: too many aliases",
    what_happened:
      "The query used more aliases than allowed (default 15). Aliases are the classic way to amplify a single query into many — pingora caps them to keep `mostExpensiveField` from being requested 100 times in one round-trip.",
    recommendations: [
      "If a legitimate batched-style client needs more aliases, raise the alias limit under API Security → GraphQL.",
      "Repeated alias-spam from one IP is a denial-of-service amplification — block under Web App Security → Firewall.",
    ],
    severity: "high",
  },
  too_many_root_fields: {
    title: "GraphQL: too many root fields",
    what_happened:
      "The query selected more top-level (root) fields than allowed (default 15). Like aliases, packing many roots into one query is a way to amplify cost.",
    recommendations: [
      "If a legitimate use case (batch dashboards) needs more, raise the limit under API Security → GraphQL.",
      "Otherwise treat the source IP as abusive and block under Web App Security → Firewall.",
    ],
    severity: "medium",
  },
  introspection_blocked: {
    title: "GraphQL: introspection blocked",
    what_happened:
      "The query asked for the introspection schema (`__schema` / `__type`) and introspection is disabled for this site. Introspection reveals every type and field — useful in dev, dangerous in prod.",
    recommendations: [
      "If a known dev tool legitimately needs introspection, enable it under API Security → GraphQL for this site only.",
      "Repeated introspection from one IP is reconnaissance — block under Web App Security → Firewall.",
    ],
    severity: "medium",
  },
  subscriptions_not_supported: {
    title: "GraphQL: subscriptions not supported",
    what_happened:
      "The query is a `subscription` operation. The pingora GraphQL layer rejects subscriptions wholesale (the WebSocket dimension is out of scope until Phase 5.x). The caller is targeting a feature pingora doesn't proxy.",
    recommendations: [
      "If your origin really does serve subscriptions, the caller needs to bypass pingora for that WebSocket — point them at the origin directly.",
      "Otherwise the caller has a bug and should be using queries or mutations instead.",
    ],
    severity: "low",
  },
  body_too_large: {
    title: "GraphQL: body too large",
    what_happened:
      "The request body exceeded the configured size cap before pingora could parse it. The query is rejected without inspection.",
    recommendations: [
      "If you have legitimate large queries, raise the GraphQL body-size cap under API Security → GraphQL.",
      "Unsolicited huge bodies from one IP look like denial-of-service — block under Web App Security → Firewall.",
    ],
    severity: "medium",
  },
};

// ─── API observations — block_reason ─────────────────────────────────
// Cross-checked 2026-06-01 against /opt/experimental-pingora/layerd-pingora/src/proxy.rs:5637-5685
// — these are the actual `block_reason` string-literals pingora writes
// to the `api-observations` CH column. The plan's hint list was
// directional, not authoritative; the values below match what the data
// plane writes.
const API = {
  spec_validation_failed: {
    title: "Blocked: spec validation failed",
    what_happened:
      "Pingora's spec validator denied this request (Deny422). The validator's own context row has the field-level reason — see the linked Spec event below.",
    recommendations: [
      "See the Matched Spec Operation section below for the failing path template and operation id.",
      "Soften the policy by switching Spec Policy → Mode to log-only while you investigate.",
    ],
    severity: "medium",
  },
  spec_route_unknown: {
    title: "Blocked: route not in spec",
    what_happened:
      "Closed-world enforcement is on — the path doesn't appear in any active spec, so pingora denied with 404 to keep the API surface honest.",
    recommendations: [
      "Open API Security → Discovery and look for an unreviewed candidate that matches — promote it to publish the endpoint.",
      "If this is reconnaissance, the source IP should be blocked under Web App Security → Firewall.",
    ],
    severity: "medium",
  },
  graphql_policy_violation: {
    title: "Blocked: GraphQL policy violation",
    what_happened:
      "Pingora's GraphQL layer denied this request (Deny400). The GraphQL context row below has the specific failure_kind (depth, complexity, aliases, etc.).",
    recommendations: [
      "See the GraphQL Operation section below for the failure_kind and observed values.",
      "Tune the relevant GraphQL limit if a legitimate caller is being clipped.",
    ],
    severity: "medium",
  },
  auth_missing_or_invalid: {
    title: "Blocked: auth missing or invalid (401)",
    what_happened:
      "Pingora's auth dispatcher returned 401 — the request either presented no credential at all, or presented one that failed validation. The Auth event below names the specific error_kind.",
    recommendations: [
      "See the Identity section below — if no identity_kind is set, the request was anonymous.",
      "Repeated 401s from one IP look like credential probing — block under Web App Security → Rate Limit.",
    ],
    severity: "high",
  },
  auth_forbidden: {
    title: "Blocked: auth forbidden (403)",
    what_happened:
      "Pingora's auth dispatcher returned 403 — the caller authenticated successfully but is forbidden from this resource (e.g. wrong audience, wrong method for the credential type).",
    recommendations: [
      "Open the Identity section below to see who authenticated.",
      "If the policy is over-strict, soften the per-route required_methods constraint under API Security → Auth.",
    ],
    severity: "medium",
  },
  authz_scope_insufficient: {
    title: "Blocked: scope insufficient",
    what_happened:
      "The caller authenticated but their scopes / roles don't include what this route requires. The authz layer (Phase 3a) returned 403.",
    recommendations: [
      "Open the Identity section below — the identity_id and identity_kind tell you who hit it.",
      "If the missing scope is one your IdP should be issuing, fix the IdP's client config.",
      "If the scope policy is too strict, relax the requirement on this route.",
    ],
    severity: "medium",
  },
  http_401: {
    title: "Origin: unauthorised (401)",
    what_happened:
      "Pingora let the request through but the origin returned 401 on its own. Either your origin has its own auth layer (in addition to pingora's), or pingora's auth is disabled and the origin caught a bad credential.",
    recommendations: [
      "If you intend pingora to handle auth, enable it under API Security → Auth and stop letting the origin re-check.",
      "If your origin is the authoritative auth layer, this row is informational — the origin's own logs have detail.",
    ],
    severity: "low",
  },
  http_403: {
    title: "Origin: forbidden (403)",
    what_happened:
      "Pingora let the request through but the origin returned 403. Origin-side ACL / scope check denied it.",
    recommendations: [
      "Cross-check with the origin's own access logs to identify the policy that fired.",
      "If you want pingora to be the authoritative authz layer, move the check up under API Security → Auth.",
    ],
    severity: "low",
  },
  http_429: {
    title: "Origin: rate limited (429)",
    what_happened:
      "The origin returned 429 — its own rate limiter denied the request. Pingora let it through, so this didn't trip the L7 rate limit on the edge.",
    recommendations: [
      "If this caller is supposed to be limited at the edge, configure a tighter rule under Web App Security → Rate Limit.",
      "If the origin's limits are too tight, raise them there.",
    ],
    severity: "low",
  },
  http_500: {
    title: "Origin: server error (500)",
    what_happened:
      "The origin returned 5xx — the application errored on its own. Pingora forwarded the failure without modification.",
    recommendations: [
      "Cross-check with the origin's application logs — pingora doesn't have detail beyond the status code.",
      "If 5xx is spiking site-wide, check origin health and incident channels.",
    ],
    severity: "medium",
  },
  http_502: {
    title: "Origin: bad gateway (502)",
    what_happened:
      "Pingora couldn't get a clean response from the upstream — connection reset, TLS handshake failure, or upstream timeout.",
    recommendations: [
      "Confirm the origin is reachable from the pingora host — DNS, network path, firewall.",
      "Persistent 502s on one route often mean the upstream pool is exhausted.",
    ],
    severity: "high",
  },
  http_503: {
    title: "Origin: service unavailable (503)",
    what_happened:
      "The origin returned 503. Often a deliberate temporary-out-of-service signal during deploys or maintenance.",
    recommendations: [
      "Confirm with the application team whether this is a known maintenance window.",
      "If 503 spikes outside maintenance windows, the upstream pool is overloaded.",
    ],
    severity: "medium",
  },
  http_504: {
    title: "Origin: gateway timeout (504)",
    what_happened:
      "Pingora gave up waiting for an upstream response. The upstream took longer than the configured upstream timeout.",
    recommendations: [
      "If this is a known-slow operation, raise the per-route upstream timeout.",
      "Persistent 504s indicate the upstream is overloaded.",
    ],
    severity: "high",
  },
};


// --- Success / passthrough -------------------------------------------
// Phase 1B only covered failure cases, which left the drawer showing
// the engineering-is-gathering fallback on every successful request.
// Most pingora-emitted CH rows are successes; operators clicking those
// should see useful copy, not a stub. severity:low for the green chip.
const SUCCESS = {
  auth_valid: {
    title: "Auth: credential validated",
    what_happened:
      "The credential presented on this request (JWT, API key, HMAC signature, mTLS cert, or session cookie) passed every check on the auth policy that applied to the matched path. Identity is captured on the row Identity card.",
    recommendations: [
      "No action required. This is a successful authentication event.",
      "Use Similar Events to investigate the same identity across other requests.",
    ],
    severity: "low",
  },
  spec_valid: {
    title: "Spec: request validated cleanly",
    what_happened:
      "The request matched an operation declared in your OpenAPI spec and validated against its parameter, header, and body schemas. No drift, no missing fields, no schema violations.",
    recommendations: [
      "No action required. This is a successful spec validation.",
      "Use Similar Events to see other traffic to the same operation.",
    ],
    severity: "low",
  },
  spec_log_only: {
    title: "Spec: validated in log-only mode",
    what_happened:
      "Spec validation ran but the policy mode is Log Only, so the verdict was recorded without rejecting the request. The Matched Spec Operation card shows what was matched (or not).",
    recommendations: [
      "Once you are confident the spec is accurate, switch the policy mode to Enforce on the API Security Schema Validation page.",
    ],
    severity: "low",
  },
  spec_unmatched: {
    title: "Spec: request did not match any declared operation",
    what_happened:
      "The request path and method did not match any operation in your OpenAPI spec. This is normal for traffic to endpoints you have not declared yet, or it can indicate a typo or unversioned path. The Did You Mean card lists the closest spec operations if there are any.",
    recommendations: [
      "If this is a legitimate endpoint, add it to your spec (or use the Discovery page to surface and import unmatched paths in bulk).",
      "If the path looks like a probe (random IDs, scanner-style paths), tighten the spec policy to Enforce to block them outright.",
    ],
    severity: "medium",
  },
  graphql_valid: {
    title: "GraphQL: query validated",
    what_happened:
      "The GraphQL operation passed every configured control. Query depth, complexity, alias count, root-field count, mutation hardening, and introspection check all stayed within the configured limits.",
    recommendations: [
      "No action required. This is a successful GraphQL request.",
      "Operation hash and (when capture is on) variables are on the GraphQL Operation card.",
    ],
    severity: "low",
  },
  graphql_log_only: {
    title: "GraphQL: validated in log-only mode",
    what_happened:
      "GraphQL controls ran but the policy mode is Log Only, so no request was rejected. The observed depth, complexity, alias, and root-field values are on the GraphQL Operation card.",
    recommendations: [
      "Once the observed values look representative, switch the GraphQL policy to Enforce on the API Security GraphQL page.",
    ],
    severity: "low",
  },
  api_allowed: {
    title: "Request allowed",
    what_happened:
      "The request was allowed through with no API security enforcement triggered. Auth, spec validation, GraphQL controls, and the WAF all returned an allow verdict (or did not apply to this route).",
    recommendations: [
      "No action required. This is a successful request.",
      "Identity, matched operation, and per-stream verdicts are visible in the cards below when populated.",
    ],
    severity: "low",
  },
};

// --- api.http_<status> bucket ----------------------------------------
const HTTP_STATUS = {
  http_400: {
    title: "Origin returned 400 Bad Request",
    what_happened:
      "The upstream origin rejected the request with HTTP 400. This is the origin verdict, not a pingora verdict. It usually means the client sent a malformed payload, an invalid query parameter, or a header the origin did not accept.",
    recommendations: [
      "Open the Response Details card to see the origin response status and size.",
      "If the request body is captured (Events Capture must be on), the Request Body Sample card shows what the client sent.",
      "When 400s spike for one client, that is usually integration drift, not an attack.",
    ],
    severity: "low",
  },
  http_404: {
    title: "Origin returned 404 Not Found",
    what_happened:
      "The upstream origin reported the requested resource does not exist. Most 404s on API routes are scanner probes or stale client links. On the spec-validation side they often appear paired with spec.path_not_found.",
    recommendations: [
      "Check the Did You Mean card if a spec is configured; it might suggest the operator-facing path the client meant.",
      "When 404s concentrate from one IP, treat as scanning and add a rate-limit rule.",
    ],
    severity: "low",
  },
  http_405: {
    title: "Origin returned 405 Method Not Allowed",
    what_happened:
      "The path exists on the origin but the HTTP method (POST, PUT, DELETE, and so on) is not allowed for it.",
    recommendations: [
      "If unexpected, check whether the spec on this path declares the method that was used.",
      "Method enumeration is a classic recon step. Repeated 405s from one IP warrant rate-limit attention.",
    ],
    severity: "low",
  },
  http_408: {
    title: "Origin returned 408 Request Timeout",
    what_happened:
      "The origin timed out reading the request. Almost always a client-side slow-loris pattern or an upstream that is overloaded.",
    recommendations: [
      "Check the Threat Context card for the source IP and ASN; slow-loris is usually clustered.",
      "If pingora sits in front of a busy origin, consider tightening the origin request-read timeout.",
    ],
    severity: "medium",
  },
  http_413: {
    title: "Origin returned 413 Payload Too Large",
    what_happened:
      "The origin rejected the request because the body exceeded the configured size limit. Usually a misconfigured client uploading more than the API supports.",
    recommendations: [
      "Document the size limit in your OpenAPI spec so clients know upfront.",
      "Repeated 413s from one client suggest integration drift; reach out to the integrator.",
    ],
    severity: "low",
  },
  http_415: {
    title: "Origin returned 415 Unsupported Media Type",
    what_happened:
      "The origin does not accept the Content-Type the client sent. Common for clients posting form data to a JSON-only endpoint.",
    recommendations: [
      "Confirm the spec declares the acceptable content types for this operation.",
      "If 415s come from one client, escalate as integration drift.",
    ],
    severity: "low",
  },
  http_422: {
    title: "Origin returned 422 Unprocessable Entity",
    what_happened:
      "The origin understood the request but could not process it. Usually a semantic validation failure (well-formed body but bad field values).",
    recommendations: [
      "If you have spec validation on, add request-body schema checks so these get caught at the edge instead of the origin.",
    ],
    severity: "low",
  },
  http_499: {
    title: "Client closed the connection",
    what_happened:
      "Non-standard but widely used 499. The client dropped the connection before the origin finished responding. Usually network instability on the client side, not an attack.",
    recommendations: [
      "When 499s cluster from one ASN or country, that is often mobile network or geographic instability rather than something to mitigate.",
    ],
    severity: "low",
  },
};
// ─── Public map + lookup ─────────────────────────────────────────────
// Keyed `${category}.${kind}` so the drawer can do a single O(1) read.
export const EVENT_META = {
  // Auth — 14 entries
  "auth.bad_signature":  AUTH.bad_signature,
  "auth.expired":        AUTH.expired,
  "auth.not_yet_valid":  AUTH.not_yet_valid,
  "auth.bad_issuer":     AUTH.bad_issuer,
  "auth.bad_audience":   AUTH.bad_audience,
  "auth.bad_alg":        AUTH.bad_alg,
  "auth.bad_hmac":       AUTH.bad_hmac,
  "auth.not_found":      AUTH.not_found,
  "auth.revoked":        AUTH.revoked,
  "auth.bad_apikey":     AUTH.bad_apikey,
  "auth.bad_password":   AUTH.bad_password,
  "auth.bad_cert":       AUTH.bad_cert,
  "auth.replay":         AUTH.replay,
  "auth.other":          AUTH.other,

  // Spec — 9 entries (mirrors src/spec_validator/ctx.rs::FailureKind)
  "spec.path_not_found":        SPEC.path_not_found,
  "spec.method_not_allowed":    SPEC.method_not_allowed,
  "spec.bad_path_param":        SPEC.bad_path_param,
  "spec.bad_query":             SPEC.bad_query,
  "spec.bad_header":            SPEC.bad_header,
  "spec.bad_body_schema":       SPEC.bad_body_schema,
  "spec.missing_required_field":SPEC.missing_required_field,
  "spec.body_too_large":        SPEC.body_too_large,
  "spec.malformed_body":        SPEC.malformed_body,

  // GraphQL — 9 entries
  "graphql.malformed":                  GRAPHQL.malformed,
  "graphql.not_persisted":              GRAPHQL.not_persisted,
  "graphql.too_deep":                   GRAPHQL.too_deep,
  "graphql.too_complex":                GRAPHQL.too_complex,
  "graphql.too_many_aliases":           GRAPHQL.too_many_aliases,
  "graphql.too_many_root_fields":       GRAPHQL.too_many_root_fields,
  "graphql.introspection_blocked":      GRAPHQL.introspection_blocked,
  "graphql.subscriptions_not_supported":GRAPHQL.subscriptions_not_supported,
  "graphql.body_too_large":             GRAPHQL.body_too_large,

  // API observations — 13 entries (the 6 pingora-emitted labels +
  // the http_<status> bucket for upstream-returned errors).
  "api.spec_validation_failed":  API.spec_validation_failed,
  "api.spec_route_unknown":      API.spec_route_unknown,
  "api.graphql_policy_violation":API.graphql_policy_violation,
  "api.auth_missing_or_invalid": API.auth_missing_or_invalid,
  "api.auth_forbidden":          API.auth_forbidden,
  "api.authz_scope_insufficient":API.authz_scope_insufficient,
  "api.http_401":                API.http_401,
  "api.http_403":                API.http_403,
  "api.http_429":                API.http_429,
  "api.http_500":                API.http_500,
  "api.http_502":                API.http_502,
  "api.http_503":                API.http_503,
  "api.http_504":                API.http_504,
  // http_<status> bucket
  "api.http_400":                HTTP_STATUS.http_400,
  "api.http_404":                HTTP_STATUS.http_404,
  "api.http_405":                HTTP_STATUS.http_405,
  "api.http_408":                HTTP_STATUS.http_408,
  "api.http_413":                HTTP_STATUS.http_413,
  "api.http_415":                HTTP_STATUS.http_415,
  "api.http_422":                HTTP_STATUS.http_422,
  "api.http_499":                HTTP_STATUS.http_499,

  // Success entries keyed off the row result
  "auth.valid":            SUCCESS.auth_valid,
  "spec.valid":            SUCCESS.spec_valid,
  "spec.log_only":         SUCCESS.spec_log_only,
  "spec.unmatched":        SUCCESS.spec_unmatched,
  "graphql.valid":         SUCCESS.graphql_valid,
  "graphql.log_only":      SUCCESS.graphql_log_only,
  "graphql.pass":          SUCCESS.graphql_valid,
  "api.allowed":           SUCCESS.api_allowed,
};

// Public lookup. `category` ∈ {"auth","spec","graphql","api"}, `kind`
// is the raw enum-label string straight off the CH row. Returns the
// canned entry or `null` for graceful fallback.
export function lookupEventMeta(category, kind) {
  if (!category || !kind) return null;
  const key = `${String(category).toLowerCase()}.${String(kind).toLowerCase()}`;
  return EVENT_META[key] || null;
}

// Severity → coloured-chip palette. Kept here rather than in the
// drawer so all consumers share one mapping. Drawer pulls these from
// `utils/colors` so they match the global Rubik palette.
export const SEVERITY_PALETTE = {
  low:    { fg: "#178F4F", bg: "rgba(31,193,107,0.12)",  border: "rgba(31,193,107,0.32)",  label: "Low" },
  medium: { fg: "#7a5b10", bg: "rgba(223,180,0,0.16)",   border: "rgba(223,180,0,0.45)",   label: "Medium" },
  high:   { fg: "#D00416", bg: "rgba(251,55,72,0.12)",   border: "rgba(251,55,72,0.32)",   label: "High" },
};

export default EVENT_META;
